import { z } from 'zod';

import { CORES_CALENDARIO as C } from './cores.generated';

/**
 * Calendario Grow uP (`growup.gg/calendario`, docs/site/calendario.md).
 *
 * O calendario nao guarda dados: cada modulo (eventos, passatempos, equipas e,
 * mais tarde, torneios e streaming) e uma "fonte" que entrega itens neste
 * formato comum. Uma feature nova so acrescenta uma fonte e uma linha em
 * `CALENDAR_KINDS`; a pagina nao muda.
 *
 * Prefixo HTTP: `GET /api/calendar?from=AAAA-MM-DD&to=AAAA-MM-DD&kinds=a,b`,
 * publico. Dias em hora de Lisboa; `to` e exclusivo.
 */

const datetimeSchema = z.string().datetime({ offset: true });
const daySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** Intervalo maximo de um pedido, em dias. */
export const MAX_CALENDAR_RANGE_DAYS = 100;

/**
 * `public` toda a gente; `members` e `private` so com sessao (mais tarde:
 * treinos da minha equipa, os meus turnos...).
 */
export const calendarVisibilitySchema = z.enum(['public', 'members', 'private']);
export type CalendarVisibility = z.infer<typeof calendarVisibilitySchema>;

export const calendarItemSchema = z.object({
  /** Tipo + id de origem (`event:<uuid>`), estavel: serve tambem ao .ics. */
  id: z.string().min(1),
  /** Texto livre; um tipo que o site nao conhece aparece como "Outros". */
  kind: z.string().min(1),
  title: z.string(),
  subtitle: z.string(),
  startsAt: datetimeSchema,
  endsAt: datetimeSchema.nullable(),
  /** Dia inteiro: mostra-se sem hora, no dia de Lisboa de `startsAt`. */
  allDay: z.boolean(),
  location: z.string(),
  online: z.boolean(),
  imageUrl: z.string().nullable(),
  /** A pagina do item no site (`/eventos/<slug>`). */
  href: z.string(),
  status: z.enum(['scheduled', 'cancelled']),
  visibility: calendarVisibilitySchema,
  /** Etiquetas curtas: "Abre", "Último dia", "Só associados"... */
  badges: z.array(z.string()),
});
export type CalendarItem = z.infer<typeof calendarItemSchema>;

export const calendarResponseSchema = z.object({
  items: z.array(calendarItemSchema),
  /** Alguma fonte falhou; os itens dela faltam. */
  partial: z.boolean(),
});
export type CalendarResponse = z.infer<typeof calendarResponseSchema>;

export const calendarQuerySchema = z
  .object({
    from: daySchema,
    to: daySchema,
    /** Separados por virgulas; vazio = todos. */
    kinds: z.string().optional(),
  })
  .refine((q) => q.to > q.from, { message: 'to tem de ser depois de from', path: ['to'] })
  .refine((q) => calendarRangeDays(q.from, q.to) <= MAX_CALENDAR_RANGE_DAYS, {
    message: `no máximo ${MAX_CALENDAR_RANGE_DAYS} dias`,
    path: ['to'],
  });
export type CalendarQuery = z.infer<typeof calendarQuerySchema>;

/** Dias entre dois `AAAA-MM-DD` (calendario, sem horas). */
export function calendarRangeDays(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

export interface CalendarKind {
  /** Nome no filtro. */
  label: string;
  /** Cor do ponto, da barra e do filtro. */
  color: string;
}

/**
 * Os tipos conhecidos, pela ordem dos filtros. Os futuros ja tem cor para a
 * fonte nova so ter de os usar. As cores vem do tokens.json do kit
 * (packages/ui/GRAFISMO.md).
 */
export const CALENDAR_KINDS: Readonly<Record<string, CalendarKind>> = {
  event: { label: 'Eventos', color: C['calendar-event'] },
  giveaway: { label: 'Passatempos', color: C['calendar-giveaway'] },
  team_match: { label: 'Jogos das equipas', color: C['calendar-team-match'] },
  tournament: { label: 'Torneios', color: C['calendar-tournament'] },
  stream: { label: 'Streams', color: C['calendar-stream'] },
  stream_party: { label: 'Stream parties', color: C['calendar-stream'] },
};

export const OTHER_CALENDAR_KIND: CalendarKind = { label: 'Outros', color: C['calendar-other'] };

export function calendarKind(kind: string): CalendarKind {
  return CALENDAR_KINDS[kind] ?? OTHER_CALENDAR_KIND;
}

/** O instante da meia-noite de Lisboa no dia `AAAA-MM-DD`. */
export function lisbonMidnight(day: string): Date {
  const utc = Date.parse(`${day}T00:00:00Z`);
  // A essa hora UTC, Lisboa esta em 00h (inverno) ou 01h (verao).
  const hour = Number(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Lisbon',
      hour: '2-digit',
      hourCycle: 'h23',
    }).format(utc),
  );
  return new Date(utc - hour * 3_600_000);
}
